import { Button, Divider, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import {
  FGCheckboxInput,
  FGCustomPanel,
  FGEmpty,
  FGSelectInput,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  showError,
  showSuccess,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import { ClubApi, ClubDto, CountryApi } from "../../../../../api";
import {
  FGCountrySelect,
  FGSRSHCheckboxInput,
  FGSRSHDateMaskInput,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../../components";
import { useDialog } from "../../../../../contexts";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useCrudApi, useTl } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";
import { vatNumberValidate } from "../../../../../utils";
import { ClubContactPage } from "./ClubContactPage";

export interface IClubDetailPageProps {
  clubId: number;
  editMode: boolean;
}

export const ClubDetailPage: React.FunctionComponent<IClubDetailPageProps> = ({ clubId, editMode }) => {
  const { t } = useTl();
  const api = useApiService(ClubApi);
  const history = useHistory();
  const countryApi = useApiService(CountryApi);
  const { showDialogPromise } = useDialog();

  const [languages, lLoading] = useReferential((a) => a.getLanguages(), true, []);
  const [saveBcLoading, setsaveBcLoading] = React.useState(false);
  const [edit, setEdit] = React.useState<boolean>();

  const { data, loading, deleteItem, deleting, saveItem, saving, refresh } = useCrudApi<ClubDto>(
    {
      getApiFn: (id = clubId) => (id > 0 ? api.get(id) : new ClubDto().toJSON()),
      saveApiFn: (d) => api.save(d),
      onSavedRoute: (d) => d && `${ERoutes.club}/${d.clubId}/detail/edit`,
      deleteApiFn: (d) => api.delete(d.clubId),
      onDeletedRoute: () => ERoutes.club,
    },
    [clubId]
  );

  const saveBc = React.useCallback(async () => {
    setsaveBcLoading(true);
    const dialogResult = await showDialogPromise({
      message: t(ETLCodes.BCSaveConfirmationMessage),
      title: t(ETLCodes.Warning),
    });
    if (dialogResult !== "yes") {
      setsaveBcLoading(false);
      return;
    }
    try {
      await api.saveBc(data?.clubId);
      showSuccess(t(ETLCodes.SaveSuccess));
      refresh();
    } catch {
      showError(t(ETLCodes.Error));
    }
    setsaveBcLoading(false);
  }, [api, data, refresh, showDialogPromise, t]);

  const FormSchema = React.useMemo(() => {
    return Yup.object().shape({
      name: Yup.string().nullable().required(t(ETLCodes.Required)),
      languageId: Yup.number().nullable().required(t(ETLCodes.Required)),
      globalEmail: Yup.string().nullable().email(t(ETLCodes.InvalidEmail)),
      vatNumber: Yup.string()
        .nullable()
        .test("validate", t(ETLCodes.InvalidVAT), async function (value) {
          if (this.parent.countryId) {
            const isBelgium = await countryApi.isBelgian(this.parent.countryId);
            if (isBelgium.value) return vatNumberValidate(value);
          }
          return true;
        }),
    });
  }, [countryApi, t]);

  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.sharepointUri, [data?.sharepointUri]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.sharepointUri) {
      window.open(data?.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await api.createInSharepoint(clubId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [api, clubId, data?.sharepointUri, refresh]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        onSubmit={saveItem}
        editMode={editMode}
        validationSchema={FormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.club)}
        onDelete={deleteItem}
        showDeleteButton={clubId > 0}
        saving={saving}
        deleting={deleting}
      >
        <FieldGroup
          columns={2}
          fieldsetProps={{
            title: t(ETLCodes.Club),
            rightElement: (
              <>
                {clubId > 0 && (
                  <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
                )}
                {data && !data.bcIdentifier ? (
                  <Button
                    icon={IconNames.CREDIT_CARD}
                    onClick={saveBc}
                    text={t(ETLCodes.SaveNav)}
                    intent={Intent.PRIMARY}
                    loading={saveBcLoading}
                  />
                ) : null}
              </>
            ),
          }}
        >
          {data?.bcIdentifier ? (
            <FGTextInput label={t(ETLCodes.BCIdentifier)} name="bcIdentifier" readonly />
          ) : (
            <FGCheckboxInput label={t(ETLCodes.BCIdentifier)} name="BcIdentifier" readonly />
          )}
          <FGTextInput label={t(ETLCodes.Name)} name="name" />
          <FGTextInput label={t(ETLCodes.AccountNumber)} name="accountNumber" />
          <FGTextInput label={t(ETLCodes.PhoneNumber)} name="phoneNumber" />
          <FGEmpty />
          <FGSelectInput label={t(ETLCodes.Language)} name="languageId" items={languages} loading={lLoading} />
          <FGEmpty />
          <FGCountrySelect label={t(ETLCodes.Country)} name="countryId" />
          <FGEmpty />
          <FGSRSHCheckboxInput name="hasLinkedBreed" label={t(ETLCodes.HasLinkedBreed)} disabled />
          <FGEmpty />
          <FGTextInput label={t(ETLCodes.GlobalEmail)} name="globalEmail" />
          <FGTextInput label={t(ETLCodes.WebsiteAdresse)} name="websiteAdresse" />
          <FGSRSHDateMaskInput label={t(ETLCodes.ExclusionStartDate)} name="exclusionStartDate" />
          <FGSRSHDateMaskInput label={t(ETLCodes.ExclusionEndDate)} name="exclusionEndDate" />
          <FGTextInput label={t(ETLCodes.ExclusionReason)} name="exclusionReason" />
          <FGEmpty />
          <FGTextInput label={t(ETLCodes.ProvisionalNumber)} name="provisionalNumber" />
          <FGSRSHDateMaskInput label={t(ETLCodes.ProvisionalNumberDate)} name="provisionalNumberDate" />
          <FGTextInput label={t(ETLCodes.FinalNumber)} name="finalNumber" />
          <FGSRSHDateMaskInput label={t(ETLCodes.FinalNumberDate)} name="finalNumberDate" />
        </FieldGroup>
        <FieldGroup columns={2}>
          <FGTextAreaInput label={t(ETLCodes.Remark)} name="remark" rows={4} />
          <FGEmpty />
        </FieldGroup>
        <Divider />
        <FieldGroup columns={2}>
          <FGTextInput
            name="vatNumber"
            label={t(ETLCodes.VATNumber)}
            maxLength={20}
            readonly={data?.bcIdentifier && data.vatNumber !== null && data.vatNumber !== ""}
          />
          <FGTextInput name="societyName" label={t(ETLCodes.SocietyName)} maxLength={50} />
        </FieldGroup>
        <FGCustomPanel>{(ctx) => <>{setEdit(ctx.editMode)}</>}</FGCustomPanel>
      </SmallFormGenerator>
      <br />
      <ClubContactPage clubId={clubId} editMode={editMode} />
    </>
  );
};
